import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import arrowIcon from '@/assets/dashboard/arrow.svg'

export interface BuySellSplitData {
  buyCount: number
  sellCount: number
}

interface BuySellSplitProps {
  data?: BuySellSplitData
  className?: string
  buyPercentage?: number // Override calculated percentage (0-100)
  sellPercentage?: number // Override calculated percentage (0-100)
  // Legacy props for backward compatibility
  buyCount?: number
  sellCount?: number
}

export const BuySellSplit = ({ 
  className,
  data,
  buyPercentage,
  sellPercentage,
  buyCount: legacyBuyCount,
  sellCount: legacySellCount
}: BuySellSplitProps) => {
  // Use data prop or legacy props
  const buyCount = data?.buyCount ?? legacyBuyCount ?? 0
  const sellCount = data?.sellCount ?? legacySellCount ?? 0

  // Calculate percentages
  const { buyPct, sellPct } = useMemo(() => {
  const total = buyCount + sellCount
    
    if (total === 0) {
      // Default to 50/50 if no data
      return { buyPct: 50, sellPct: 50 }
    }

    // Use provided percentages or calculate from counts
    if (buyPercentage !== undefined && sellPercentage !== undefined) {
      return { 
        buyPct: Math.round(buyPercentage), 
        sellPct: Math.round(sellPercentage) 
      }
    }

    const calculatedBuyPct = Math.round((buyCount / total) * 100)
    const calculatedSellPct = 100 - calculatedBuyPct

    return { 
      buyPct: calculatedBuyPct, 
      sellPct: calculatedSellPct 
    }
  }, [buyCount, sellCount, buyPercentage, sellPercentage])

  // Colors from Figma design
  const buyStripeColor = '#1fb845' // Green for stripes
  const sellStripeColor = '#ff540c' // Orange for stripes (matches SELL badge color)

  return (
    <div className={cn('relative w-full flex flex-col justify-between', className)}>
      {/* Header: Icon and Label - top left, gap-[6px] */}
      <div className="flex items-center gap-[6px]">
        {/* Icon: size-[16px] */}
        <img src={arrowIcon} alt="Buy/Sell Split" className="w-4 h-4 dark:[filter:brightness(0)_invert(1)_opacity(0.72)]" />
        {/* Label: text-[13px] leading-[18px], font-medium */}
        <span className="font-plus-jakarta font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
          Buy/Sell Split
        </span>
      </div>

      {/* Bottom row - Percentages and Progress Bar at bottom */}
      <div className="relative flex items-center justify-between">
        {/* Left: Buy percentage and badge */}
        <div className="flex items-center gap-[4px]">
          {/* Percentage: text-[28px] leading-[32px] tracking-[-0.2px] */}
          <span className="font-plus-jakarta font-medium text-[28px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
            {buyPct}%
          </span>
          {/* BUY Badge: bg-[#1fb845], px-[6px] py-[4px], rounded-[16px] */}
          <div className="bg-[#1fb845] flex items-center justify-center px-[6px] py-[4px] rounded-[16px]">
            <span className="font-plus-jakarta font-semibold text-[11px] leading-[12px] text-white">
              BUY
            </span>
          </div>
        </div>

        {/* Center: Progress Bar - h-[32px] centered */}
        <div className="absolute left-1/2 -translate-x-1/2 h-[32px] w-[221px] overflow-hidden">
          <div className="flex h-full w-full">
            {/* Buy portion - Green stripes */}
            {buyPct > 0 && (
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${buyPct}%` }}
                transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="h-full"
              >
                <div 
                  className="h-full w-full"
                  style={{
                    backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 1.5px, ${buyStripeColor} 1.5px, ${buyStripeColor} 3px)`
                  }}
                />
              </motion.div>
            )}
            
            {/* Sell portion - Orange/Red stripes */}
            {sellPct > 0 && (
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${sellPct}%` }}
                transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="h-full"
              >
                <div 
                  className="h-full w-full"
                  style={{
                    backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 1.5px, ${sellStripeColor} 1.5px, ${sellStripeColor} 3px)`
                  }}
                />
              </motion.div>
            )}
          </div>
        </div>

        {/* Right: Sell badge and percentage */}
        <div className="flex items-center gap-[4px]">
          {/* SELL Badge: bg-[#ff540c], px-[6px] py-[4px], rounded-[16px] */}
          <div className="bg-[#ff540c] flex items-center justify-center px-[6px] py-[4px] rounded-[16px]">
            <span className="font-plus-jakarta font-semibold text-[11px] leading-[12px] text-white">
              SELL
            </span>
          </div>
          {/* Percentage: text-[28px] leading-[32px] tracking-[-0.2px] */}
          <span className="font-plus-jakarta font-medium text-[28px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
            {sellPct}%
          </span>
        </div>
      </div>
    </div>
  )
}

